/**
 * Reference Registry
 *
 * Second-stage dispatch for reference fields. The field component registry
 * routes reference subtypes onto `ReferenceInput` (form/filter) and
 * `ReferenceField` (cell/grid); this registry says how that component behaves per
 * subtype — which chip `element` renders and (optionally) how it `search`es.
 * Unlike junctions there's no row to create/delete: `modelValue` stores ids and
 * the target row IS the thing to render.
 *
 * `search`/`placeholder` are optional: display-only reference types (e.g.
 * `source`, managed through their own ingestion/linking flows, never edited
 * inline) register just an `element` and render read-only chips — the cell
 * shows no picker without a `search`.
 *
 * A subtype with no entry takes its nearest ancestor's (`referenceEntry`: image →
 * attachment). `ReferenceField` also serves link fields — they have no entry of
 * their own here, so they fall back to the `record` entry (link and record
 * both point at records; only the persistence path differs — link reconciles rows
 * through `@auger/core`'s records.update).
 *
 * The registry is open. This package registers the entries whose chips it owns
 * (see ../defaults); an app registers the rest, and the `search` functions —
 * they are where an app's data access lives (its clients and stores). A field
 * whose filter limits its picker reads that scope from the app too, through
 * registerReferenceScope.
 */
import type { Component } from "vue"
import {
  type ContactType,
  FIELD_TYPES,
  getFieldType,
  type JunctionType,
  type ReferenceType,
  type SavedFilter,
} from "@auger/core/fields"

// The target a reference chip displays: a record or a user. Their only
// guaranteed common shape is an id + optional name, so this is the lowest
// common denominator across those heterogeneous target tables.
export interface ReferenceEntity {
  id: number | string
  name?: string
  [k: string]: unknown
}

// A picker's search scope: the field's record type, and a link field's filter
// on the records it can link to.
export interface SearchOptions {
  recordType?: string | null
  filter?: SavedFilter | null
}

type ScopedField = { recordType?: string | null; filter?: SavedFilter } | null | undefined

/** The scope a picker searches within, from its field. */
export function searchOptions(field: ScopedField): SearchOptions {
  return { recordType: field?.recordType ?? null, filter: field?.filter ?? null }
}

/** The warning on a link a field's filter would no longer offer. */
export const OUTSIDE_FILTER = "Outside this field's filter: it stays, but couldn't be picked again"

export interface ReferenceScope {
  /** What a filtered link field's picker is limited to (the tooltip on its
   *  filter icon), or null when it offers every record of its type. */
  filterNote: (field: ScopedField) => string | null
  /** Which targets of a filtered link field its filter no longer offers. */
  linksOutsideFilter: (field: () => ScopedField) => { isOutside: (id: number | string) => boolean }
}

/** The app's answers about a filtered field; with none registered, no field
 *  shows a filter note or an outside-filter warning. */
export const REFERENCE_SCOPE: ReferenceScope = {
  filterNote: () => null,
  linksOutsideFilter: () => ({ isOutside: () => false }),
}

export function registerReferenceScope(scope: Partial<ReferenceScope>): void {
  Object.assign(REFERENCE_SCOPE, scope)
}

export interface ReferenceRegistryEntry {
  // The chip element rendered per resolved target.
  element: Component

  // Picker affordances — optional so display-only reference types (no inline
  // editing) can register just an `element`. Absent `search` → no picker.
  placeholder?: string
  search?: (query: string, opts: SearchOptions) => Promise<ReferenceEntity[]>
}

export type ReferenceEntryName = ReferenceType | JunctionType | ContactType

/** Keyed by type, `Partial` because only the types rendered by ReferenceField /
 *  ReferenceInput need an entry. */
export const REFERENCE_REGISTRY: Partial<Record<ReferenceEntryName, ReferenceRegistryEntry>> = {}

/**
 * Add entries to the registry. An entry merges into the type's existing one, so
 * an app can add a `search` to an entry this package registered with just an
 * `element`; the merged entry must end up with an `element`.
 */
export function registerReferenceEntries(
  entries: Partial<Record<ReferenceEntryName, Partial<ReferenceRegistryEntry>>>
): void {
  for (const [name, entry] of Object.entries(entries) as [
    ReferenceEntryName,
    Partial<ReferenceRegistryEntry>,
  ][]) {
    REFERENCE_REGISTRY[name] = { ...REFERENCE_REGISTRY[name], ...entry } as ReferenceRegistryEntry
  }
}

/** A field type's entry: its own, else its nearest ancestor's (image →
 *  attachment), else `record`'s — link and unregistered types render records. */
export function referenceEntry(type: string | null | undefined): ReferenceRegistryEntry {
  let current = type ? getFieldType(type) : undefined
  while (current) {
    const entry = REFERENCE_REGISTRY[current.name as ReferenceEntryName]
    if (entry) return entry
    current = current.parent ? FIELD_TYPES[current.parent] : undefined
  }
  return REFERENCE_REGISTRY.record as ReferenceRegistryEntry
}
